import React, { useCallback, useEffect, useRef, useState } from "react";
import { HexColorPicker } from "react-colorful";
import useClickOutside from "./useClickOutside";
import "./ColorPicker.scss";

// Utility functions
const hexToRgb = (hex) => {
  const input_hex = hex[0] === "#" ? hex.slice(1) : hex
  const bigint = parseInt(input_hex, 16);
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  };
};

const rgbToHex = ({ r, g, b }) => {
  return (
    "#" +
    [r, g, b]
      .map((x) => {
        const hex = x.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("")
  );
};

const ColorPicker = ({ color, onChange }) => {
  const popover = useRef();
  const [isOpen, toggle] = useState(false);
  const [rgb, setRgb] = useState(hexToRgb(color));
  const [hex, setHex] = useState(color);

  const close = useCallback(() => toggle(false), []);
  useClickOutside(popover, close);

  const presetColors = ["#FF0000", "#FFFF00", "#00FF00", "#00FFFF", "#0000FF", "#FF00FF"];

  useEffect(() => {
    setRgb(hexToRgb(color));
    setHex(color);
  }, [color]);

  const handlePickerChange = (newHex) => {
    setHex(newHex);
    setRgb(hexToRgb(newHex));
    onChange(newHex);
  };

  const handleRgbChange = (channel, value) => {
    const updated = { ...rgb, [channel]: Math.min(255, Math.max(0, Number(value) || 0)) };
    const newHex = rgbToHex(updated);
    setRgb(updated);
    setHex(newHex);
    onChange(newHex);
  };

  const handleHexChange = (input) => {
    const cleaned = input.trim().toLowerCase();
    if (!/^#?[0-9a-f]*$/i.test(cleaned)) return;
    setHex(cleaned.startsWith("#") ? cleaned : "#" + cleaned);
    setRgb(hexToRgb(cleaned));
  };
  
  const handleHexBlur = () => {
    let clean = hex.trim().replace(/^#/, "");
    if (/^[0-9A-F]{6}$/.test(clean)) {
      const normalized = `#${clean}`;
      setHex(normalized);
      setRgb(hexToRgb(normalized));
      onChange(normalized);
      return;
    }
    if (/^[0-9A-F]{3}$/.test(clean)) {
      const expanded = clean.split("").map(c => c + c).join("");
      const normalized = `#${expanded}`;
      setHex(normalized);
      setRgb(hexToRgb(normalized));
      onChange(normalized);
      return;
    }
    const fallback = rgbToHex(rgb);
    setHex(fallback);
    onChange(fallback);
  };

  return (
    <div className="popup-picker">
      <div
        className="color-swatch"
        style={{ backgroundColor: color }}
        onClick={() => toggle(true)}
      />

      {isOpen && (
        <div className="color-picker" ref={popover}>
          <HexColorPicker width={"100%"} color={color} onChange={handlePickerChange} presetColors={presetColors}/>
          <div className="picker-presets">
            {presetColors.map((presetColor) => (
              <button
                key={presetColor}
                className="picker-preset"
                style={{ background: presetColor }}
                onClick={() => onChange(presetColor)}
              />
            ))}
          </div>
          <div className="input-color input-spaced">
            <label>R</label>
            <input
              className="input-rgb"
              type="number"
              value={rgb.r}
              min="0"
              max="255"
              onChange={(e) => handleRgbChange("r", e.target.value)}
            />
            <label>G</label>
            <input
              className="input-rgb"
              type="number"
              value={rgb.g}
              min="0"
              max="255"
              onChange={(e) => handleRgbChange("g", e.target.value)}
            />
            <label>B</label>
            <input
              className="input-rgb"
              type="number"
              value={rgb.b}
              min="0"
              max="255"
              onChange={(e) => handleRgbChange("b", e.target.value)}
            />
          </div>
          <div className="input-color">
            <label>HEX</label>
            <input
              className="input-hex"
              type="text"
              maxLength="7"
              value={hex.toUpperCase()}
              onChange={(e) => handleHexChange(e.target.value)}
              onBlur={handleHexBlur}
            >
              </input>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorPicker;
